import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData, VerifyData } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Add, Edit, Visibility, Delete, Inventory, FilterList, Refresh, CheckCircle, Cancel, Close
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box
} from '@mui/material';
import StockTransferInModal from './StockTransferInModal';
import './StockTransferInIndex.css';

const StockTransferInIndex = () => {
  // Session validation
  useSessionValidation();
  
  const history = useHistory();
  const { companyId, branchId, token, userId, permissions } = useAuth();

  const hasCreateMasterPermission = permissions?.includes('Create Master');
  const hasEditMasterPermission = permissions?.includes('Edit Master');
  const hasDeleteMasterPermission = permissions?.includes('Delete Master');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [stockTransfers, setStockTransfers] = useState([]);
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());
  
  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [transferDetails, setTransferDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate]);

  // Fetch functions
  const fetchStockTransfers = async () => {
    try {
      const activeResponse = await fetchData('StockTransferInApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));
      
      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('StockTransferInApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive stock transfers:', inactiveError);
      }
      
      setStockTransfers([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching stock transfers:', error);
      setStockTransfers([]);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await fetchData('BranchApi', `/${companyId}`, token);
      setBranches(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
    }
  };

  const fetchMaxTransferNo = async () => {
    try {
      const response = await fetchData('StockTransferInApi', `/MaxTransferNo/${companyId}/${branchId || 1}`, token);
      const data = response.response_data || response || {};
      const transferNumber = data.TransferNumber || data.transferNumber || 1;
      const transferPrefix = data.TransferPrefix || data.transferPrefix || 'STI';
      const finYear = data.FinYear || data.finYear || '';
      
      return { transferNumber, transferPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max transfer number:', error);
      return { transferNumber: 1, transferPrefix: 'STI', finYear: '' };
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Loading stock transfers...');
    try {
      await Promise.allSettled([
        fetchStockTransfers(), 
        fetchItems(), 
        fetchBranches()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSingleStockTransfer = async (transferId) => {
    try {
      const response = await fetchData('StockTransferInApi', `/${transferId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single stock transfer:', error);
      return null;
    }
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);
      const freshData = await fetchSingleStockTransfer(itemId);

      if (freshData) {
        const transferData = freshData.StockTransferIn || freshData;
        const details = freshData.StockTransferInDetails || [];
        
        setSelectedItem(transferData);
        setFormData({
          Id: transferData.Id,
          CompanyId: transferData.CompanyId || companyId,
          BranchId: transferData.BranchId || branchId,
          FromBranchId: transferData.FromBranchId,
          TransferNumber: transferData.TransferNumber,
          TransferPrefix: transferData.TransferPrefix || '',
          FinYear: transferData.FinYear || '',
          TransferDate: transferData.TransferDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          TotalQuantity: transferData.TotalQuantity || 0,
          TotalAmount: transferData.TotalAmount || 0,
          Remarks: transferData.Remarks || '',
          IsVerified: transferData.IsVerified || false,
          IsActive: transferData.IsActive ?? true,
          CreatedBy: transferData.CreatedBy,
          CreatedDate: transferData.CreatedDate,
          CreatedByName: transferData.CreatedByName,
          ModifiedBy: transferData.ModifiedBy,
          ModifiedDate: transferData.ModifiedDate,
          ModifiedByName: transferData.ModifiedByName
        });
        setTransferDetails(details.map(d => ({
          Id: d.Id || 0,
          ItemId: d.ItemId,
          Quantity: d.Quantity || 0,
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: d.CreatedBy,
          CreatedDate: d.CreatedDate,
          ModifiedBy: d.ModifiedBy,
          ModifiedDate: d.ModifiedDate
        })));
      }
      setModalLoading(false);
    } else {
      // Create mode
      setModalLoading(true);
      const maxTransferData = await fetchMaxTransferNo();
      
      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        FromBranchId: '',
        TransferNumber: maxTransferData.transferNumber,
        TransferPrefix: maxTransferData.transferPrefix,
        FinYear: maxTransferData.finYear,
        TransferDate: new Date().toISOString().split('T')[0],
        TotalQuantity: 0,
        TotalAmount: 0,
        Remarks: '',
        IsVerified: false,
        IsActive: true
      });
      setTransferDetails([]);
      setModalLoading(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setTransferDetails([]);
  };

  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} stock transfer...`);

      const dataToSend = {
        StockTransferIn: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || branchId,
          FromBranchId: parseInt(formData.FromBranchId) || 0,
          TransferNumber: formData.TransferNumber || '',
          TransferPrefix: formData.TransferPrefix || '',
          FinYear: formData.FinYear || '',
          TransferDate: formData.TransferDate || new Date().toISOString().split('T')[0],
          TotalQuantity: parseFloat(formData.TotalQuantity) || 0,
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          Remarks: formData.Remarks || '',
          IsVerified: formData.IsVerified || false,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          CreatedDate: modalAction === 'create' ? null : formData.CreatedDate,
          ModifiedBy: modalAction === 'edit' ? userId : null
        },
        StockTransferInDetails: transferDetails.map(detail => ({
          Id: modalAction === 'edit' ? (detail.Id || 0) : 0,
          CompanyId: companyId,
          StockTransferInId: modalAction === 'edit' ? formData.Id : 0,
          ItemId: parseInt(detail.ItemId) || 0,
          Quantity: parseFloat(detail.Quantity) || 0,
          Rate: parseFloat(detail.Rate) || 0,
          Amount: parseFloat(detail.Amount) || 0,
          IsActive: detail.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
          CreatedDate: modalAction === 'create' ? null : detail.CreatedDate,
          ModifiedBy: modalAction === 'edit' ? userId : null
        }))
      };

      if (modalAction === 'create') {
        await postData('StockTransferInApi', '', dataToSend, token);
        setSuccess('Stock Transfer In created successfully!');
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Stock Transfer ID is missing');
        await updateData('StockTransferInApi', formData.Id, dataToSend, token);
        setSuccess('Stock Transfer In updated successfully!');
      }

      handleCloseModal();
      await fetchStockTransfers();
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} stock transfer. Please try again.`);
      setLoading(false);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, transferNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(transferNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('StockTransferInApi', deleteItemId, token);
      setSuccess('Stock Transfer In deleted successfully!');
      await fetchStockTransfers();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete stock transfer.');
    }
    setLoading(false);
  };

  // Activate handlers
  const handleOpenActivateModal = (id, transferNo) => {
    setActivateItemId(id);
    setActivateItemNo(transferNo);
    setActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setActivateItemId(null);
    setActivateItemNo('');
  };

  const handleActivate = async () => {
    if (!activateItemId) return;

    setLoading(true);
    handleCloseActivateModal();
    try {
      await activateData('StockTransferInApi', activateItemId, token);
      setSuccess('Stock Transfer In activated successfully!');
      await fetchStockTransfers();
    } catch (error) {
      console.error('Error activating:', error);
      setError('Failed to activate stock transfer.');
    }
    setLoading(false);
  };

  // Filter and pagination
  const getFilteredData = (data) => {
    if (!Array.isArray(data)) return [];
    
    let filtered = [...data];

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.TransferNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branches.find(b => b.Id === item.FromBranchId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item =>
        filterStatus === 'active' ? item.IsActive : !item.IsActive
      );
    }

    if (filterVerified !== 'all') {
      filtered = filtered.filter(item =>
        filterVerified === 'verified' ? item.IsVerified : !item.IsVerified
      );
    }

    return filtered;
  };

  const getPaginatedData = (data) => {
    const filtered = getFilteredData(data) || [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    
    return {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };
  const handleRefresh = () => fetchAllData();
  const handleCloseSnackbar = () => { setError(''); setSuccess(''); };

  // Date change handlers
  const handleFromDateChange = (e) => {
    setFromDate(e.target.value);
    setCurrentPage(1);
  };

  const handleToDateChange = (e) => {
    setToDate(e.target.value);
    setCurrentPage(1);
  };

  // Format helpers
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(stockTransfers);

  return (
    <div className="stock-transfer-in-dashboard">
      {loading && <LoadingOverlay message={loadingMessage} />}
      
      <div className="dashboard-header">
        <h5 className="plain-header">Stock Transfer In Management</h5>
      </div>

      <div className="dashboard-content">
        <div className="data-table-container">
          <div className="table-header">
            <div className="search-filter-section">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search by Transfer No, Branch..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>
              
              <div className="date-filter">
                <input
                  type="date"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  title="From Date"
                />
                <input
                  type="date"
                  value={toDate}
                  onChange={handleToDateChange}
                  title="To Date"
                />
              </div>
              
              <div className="filter-section">
                <FilterList className="filter-icon" />
                <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="filter-section">
                <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              
              <Button variant="outlined" onClick={handleRefresh} disabled={loading} className="refresh-button desktop-only" startIcon={<Refresh />}>
                Refresh
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal('create')} className="add-button desktop-only" disabled={!hasCreateMasterPermission}>
                Add Transfer In
              </Button>
              
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={loading} className="refresh-icon-btn mobile-only">
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Transfer In">
                <IconButton onClick={() => handleOpenModal('create')} className="add-icon-btn mobile-only" disabled={!hasCreateMasterPermission}>
                  <Add />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sl No.</th>
                  <th>Transfer No.</th>
                  <th>Date</th>
                  <th>From Branch</th>
                  <th>Total Qty</th>
                  <th>Total Amount</th>
                  <th>Verified</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <tr key={item.Id}>
                    <td>{startIndex + index + 1}</td>
                    <td>
                      <strong>
                        {item.TransferPrefix && item.FinYear 
                          ? `${item.TransferPrefix}/${item.TransferNumber}/${item.FinYear}`
                          : item.TransferNumber || '-'}
                      </strong>
                    </td>
                    <td>{formatDate(item.TransferDate)}</td>
                    <td>{branches.find(b => b.Id === item.FromBranchId)?.Name || '-'}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{item.TotalQuantity || 0}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#059669' }}>{formatCurrency(item.TotalAmount)}</td>
                    <td>
                      <Chip 
                        label={item.IsVerified ? 'Verified' : 'Pending'} 
                        color={item.IsVerified ? 'success' : 'warning'} 
                        size="small" 
                      />
                    </td>
                    <td>
                      <Chip 
                        label={item.IsActive ? 'Active' : 'Inactive'} 
                        color={item.IsActive ? 'success' : 'default'} 
                        size="small" 
                      />
                    </td>
                    <td>
                      <div className="action-buttons">
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => handleOpenModal('view', item)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {hasEditMasterPermission && item.IsActive && (
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleOpenModal('edit', item)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasDeleteMasterPermission && (
                          <>
                            {item.IsActive ? (
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => handleOpenDeleteModal(item.Id, item.TransferNumber)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Activate">
                                <IconButton size="small" onClick={() => handleOpenActivateModal(item.Id, item.TransferNumber)}>
                                  <CheckCircle fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {paginatedData.length === 0 && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      No stock transfers found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-section">
            <div className="pagination-info">
              <span>Showing {startIndex + 1} to {Math.min(startIndex + rowsPerPage, totalCount)} of {totalCount} entries</span>
              <div className="rows-per-page">
                <label>Show:</label>
                <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
            <div className="pagination-controls">
              <button 
                className="pagination-btn" 
                onClick={() => handlePageChange(1)} 
                disabled={currentPage === 1}
              >
                First
              </button>
              <button 
                className="pagination-btn" 
                onClick={() => handlePageChange(currentPage - 1)} 
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                return (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                );
              })}
              <button 
                className="pagination-btn" 
                onClick={() => handlePageChange(currentPage + 1)} 
                disabled={currentPage === totalPages}
              >
                Next
              </button>
              <button 
                className="pagination-btn" 
                onClick={() => handlePageChange(totalPages)} 
                disabled={currentPage === totalPages}
              >
                Last
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stock Transfer In Modal */}
      {openModal && (
        <StockTransferInModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          transferDetails={transferDetails}
          setTransferDetails={setTransferDetails}
          onSave={handleSave}
          items={items}
          branches={branches}
          loading={modalLoading}
          companyId={companyId}
          branchId={branchId}
          token={token}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fef2f2', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Delete sx={{ color: '#dc2626', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Delete Stock Transfer</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to delete stock transfer <strong>{deleteItemNo}</strong>?
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ borderColor: '#d1d5db', color: '#6b7280' }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Modal */}
      <Dialog open={activateModalOpen} onClose={handleCloseActivateModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#f0fdf4', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle sx={{ color: '#16a34a', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Activate Stock Transfer</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to activate stock transfer <strong>{activateItemNo}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseActivateModal} variant="outlined" sx={{ borderColor: '#d1d5db', color: '#6b7280' }}>Cancel</Button>
          <Button onClick={handleActivate} variant="contained" sx={{ backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}>Activate</Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
    </div>
  );
};

export default StockTransferInIndex;